import { cloneDeep, isEmpty } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import {
  createTargetingPresetHeaderBidder,
  defaultTargetingPresetHeaderBidder,
  getTargetingPresetHeaderBidder,
  ITargetingPresetHeaderBidder,
  patchTargetingPresetHeaderBidder,
} from '~/src/dash/apiV2/targeting/targetingPresets';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { FormBuilder } from '~/src/dash/components/forms/formBuilder';
import { validateName } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/common';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { IDNameTableModal } from '~/src/dash/components/modals/IDNameTableModal';
import {
  ButtonV2,
  Col,
  Cols,
  Div,
  Panel,
  Panels,
  Row,
  Rows,
  spacing,
  Span,
  Text,
  TextInput,
} from '~/src/dash/publicaUI';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  parseLocation,
  RouteBidderSettingsExisting,
  RouteTargetingList,
} from '~/src/dash/router/routes';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import { SnackbarView } from '~/src/dash/views/snackbar-view';
import { UpdateTargetingPresetBidderDialog } from '~/src/dash/views/tools/TargetingPresets/bidder-targeting/BidderTargetingFormView/UpdateTargetingPresetBidderDialog';
import {
  generateTargetingPresetParameterOptions,
  getBidderTargetingEditRoute,
} from '~/src/dash/views/tools/TargetingPresets/bidder-targeting/common';
import {
  ITargetingPresetHeaderBidderFormConfig,
  ITargetingPresetParam,
  TargetingPresetParameters,
  TargetingPresetParamKey,
} from '~/src/dash/views/tools/TargetingPresets/bidder-targeting/config';
import { BidderTargetingPresetFormContext } from '~/src/dash/views/tools/TargetingPresets/contexts';
import { TopBar } from '~/src/dash/views/topbar/TopBar';

const labelRowHeight = 32;
const iconColWidth = 16;

export const extractActiveSuperUserParamKeys = (
  paramKeys: string | undefined,
  paramCfg: Record<TargetingPresetParamKey, ITargetingPresetParam>,
) => {
  const activeSuperUserParamKeys: TargetingPresetParamKey[] = [];
  if (paramKeys) {
    paramKeys.split(',').forEach((paramKey) => {
      if (paramCfg[paramKey as TargetingPresetParamKey].isSuperUserOnly) {
        activeSuperUserParamKeys.push(paramKey as TargetingPresetParamKey);
      }
    });
  }
  return activeSuperUserParamKeys;
};

export const BidderTargetingFormView = () => {
  const { session, user, isSuperUser, location } = useBaseStore();
  const loc = parseLocation(location.pathname);
  const isNewPreset = !loc.targetingbidderid;
  const isClonedPreset = loc.presetid !== '';

  const { Tools } = useBaseConfig();
  const cfg = Tools.Targeting.Bidder as ITargetingPresetHeaderBidderFormConfig;

  const [seed, setSeed] = useState(1);
  const [loadState, setLoadState] = useState(LoadingStateLoading);
  const [listsLoadState, setListsLoadState] = useState(LoadingStateLoading);
  const [lists, setLists] = useState<Record<string, ISelectOption[]>>({});
  const [preset, setPreset] = useState(
    cloneDeep(defaultTargetingPresetHeaderBidder as ITargetingPresetHeaderBidder),
  );
  const [name, setName] = useState('');
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [hiddenActiveParamKeys, setHiddenActiveParamKeys] = useState<TargetingPresetParamKey[]>([]); // if not superuser, we preserve active superuser params

  // update dialog
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [updatePayload, setUpdatePayload] = useState<Partial<ITargetingPresetHeaderBidder>>({});

  const [openAssociatedBidders, setOpenAssociatedBidders] = useState(false);

  useEffect(() => {
    setName('');
    populateDropdowns();
    if (isClonedPreset) {
      getExistingPresetInfo(String(loc.presetid || 0));
    } else if (isNewPreset) {
      const defaultPreset = applyPresetCustoms(
        cloneDeep(defaultTargetingPresetHeaderBidder) as ITargetingPresetHeaderBidder,
      );
      unstable_batchedUpdates(() => {
        setPreset(defaultPreset);
        setLoadState(LoadingStateHasData);
        setSeed(seed + 1);
      });
    } else {
      getExistingPresetInfo(String(loc.targetingbidderid || 0));
    }
  }, [location.pathname]);

  const applyPresetCustoms = (preset: ITargetingPresetHeaderBidder) => {
    Object.values(cfg.params).forEach((param) => {
      if (!isEmpty(param.customUIConversions)) {
        const { apiKey, uiKey, convertResponse } = param.customUIConversions;
        // @ts-ignore // this is required because there cannot be a specific typing. this is handled in the config
        preset[uiKey] = convertResponse(preset[apiKey], session);
      }
    });
    return preset;
  };

  const getExistingPresetInfo = (presetID: string) => {
    setLoadState(LoadingStateLoading);
    getTargetingPresetHeaderBidder(session.publisher.ID, presetID)
      .then((res) => {
        if (res.Preset && !isEmpty(res.Preset)) {
          const completePreset = applyPresetCustoms(res.Preset);
          unstable_batchedUpdates(() => {
            setPreset(completePreset || ({} as ITargetingPresetHeaderBidder));
            setName(isClonedPreset ? res.Preset?.Name + ' - Copy' : res.Preset?.Name || '');
            setLoadState(LoadingStateHasData);
            if (!isSuperUser) {
              setHiddenActiveParamKeys(
                extractActiveSuperUserParamKeys(res.Preset?.ActiveParameters, cfg.params),
              );
            }
          });
        } else {
          unstable_batchedUpdates(() => {
            setLoadState(LoadingStateNoData);
            setSnackbarMessage('Preset information not found.');
            setSnackbarError(true);
          });
        }
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setPreset({} as ITargetingPresetHeaderBidder);
          setLoadState(LoadingStateNoData);
          setSnackbarMessage('Preset not found.');
          setSnackbarError(true);
        });
        navigate(RouteTargetingList, { p: session.publisher.ID });
      });
  };

  const populateDropdowns = () => {
    setListsLoadState(LoadingStateLoading);
    const keys: string[] = [];
    const getListFns: Array<Promise<ISelectOption[]>> = [];
    Object.values(cfg.dropdowns).forEach((dropdown) => {
      keys.push(dropdown.key);
      getListFns.push(dropdown.getList(session, user));
    });
    const tempLists: Record<string, ISelectOption[]> = {};
    Promise.all(getListFns)
      .then((results) => {
        results.forEach((result, i) => {
          tempLists[keys[i]] = result;
        });
        unstable_batchedUpdates(() => {
          setLists(tempLists);
          setListsLoadState(!isEmpty(tempLists) ? LoadingStateHasData : LoadingStateNoData);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setLists({});
          setListsLoadState(LoadingStateNoData);
          setSnackbarError(true);
          setSnackbarMessage('Error occurred while trying to load data.');
        });
      });
  };

  const handleOnSave = () => {
    // prevent double save
    if (isSaveDisabled) {
      return;
    } else {
      setIsSaveDisabled(true);
    }

    // validate name
    const { sanitizedName, nameError } = validateName(name);
    if (nameError) {
      unstable_batchedUpdates(() => {
        setSnackbarMessage(nameError);
        setSnackbarError(true);
        setIsSaveDisabled(false);
      });
      return;
    }

    // do not continue code if there are no selected parameters
    if (!preset.ActiveParameters) {
      unstable_batchedUpdates(() => {
        setSnackbarMessage('You must add at least one parameter to continue.');
        setSnackbarError(true);
        setIsSaveDisabled(false);
      });
      return;
    }

    const payload = {} as ITargetingPresetHeaderBidder;
    const tempActiveParametersArray = preset.ActiveParameters.split(',');
    const errors: string[] = [];

    // get active parameters
    tempActiveParametersArray.forEach((paramKey) => {
      const parameterInfo = TargetingPresetParameters[paramKey as TargetingPresetParamKey];

      if (!isEmpty(parameterInfo.customUIConversions)) {
        const { apiKey, uiKey, convertPayload } = parameterInfo.customUIConversions;
        const convertedResult = convertPayload(preset[uiKey as keyof ITargetingPresetHeaderBidder]);
        // @ts-ignore
        payload[apiKey] = convertedResult.value;
        errors.push(...convertedResult.errors);
      }

      // custom validations
      if (!isEmpty(parameterInfo.customValidation)) {
        const { validateKey, validateFn } = parameterInfo.customValidation;
        errors.push(...validateFn(preset[validateKey], parameterInfo.label));
      }

      if (!isEmpty(parameterInfo.apiKeys)) {
        parameterInfo.apiKeys?.forEach((apiKey) => {
          // @ts-ignore
          payload[apiKey] = preset[apiKey];
        });
      }
    });

    if (!isEmpty(errors)) {
      unstable_batchedUpdates(() => {
        setFormErrors(errors);
        setSnackbarMessage('Please resolve all errors before continuing.');
        setSnackbarError(true);
        setIsSaveDisabled(false);
      });
      return;
    }

    // preserve superuser options when user is not a superuser
    if (!isSuperUser && !isEmpty(hiddenActiveParamKeys)) {
      hiddenActiveParamKeys.forEach((apiKey) => {
        tempActiveParametersArray.push(apiKey);
        // @ts-ignore
        payload[apiKey] = preset[apiKey];
      });
    }

    // update vars not associated with form builder
    payload.PublisherID = Number(session.publisher.ID);
    payload.Name = sanitizedName;
    payload.ActiveParameters = tempActiveParametersArray.join(',');

    // revert missing parameters to their default values
    const finalPayload = { ...defaultTargetingPresetHeaderBidder, ...payload };

    // DO NOT SAVE IF NO CHANNELS OR CHANNEL GROUPS ARE FOUND
    // if (isEmpty(payload.SiteIDs) && isEmpty(payload.ChannelGroupIDs)) {
    //   unstable_batchedUpdates(() => {
    //     setSnackbarError(true);
    //     setSnackbarMessage('A Channel or Channel Group must be selected.');
    //     setFormErrors(['You must add a Channel/Channel Group.']);
    //     setIsSaveDisabled(false);
    //   });
    //   return;
    // }

    if (isNewPreset || isClonedPreset) {
      createTargetingPresetHeaderBidder(session.publisher.ID, finalPayload)
        .then((res) => {
          setIsSaveDisabled(false);
          navigate(getBidderTargetingEditRoute(res.ID, session, user));
          setSnackbarMessage(`Preset ${sanitizedName} has been successfully created.`);
          setSnackbarError(false);
          setFormErrors([]);
        })
        .catch((err) => {
          console.error(err);
          unstable_batchedUpdates(() => {
            setSnackbarMessage('An error has occurred. Please check your values and try again.');
            setSnackbarError(true);
            setIsSaveDisabled(false);
          });
        });
    } else {
      finalPayload.ID = preset.ID;
      // if preset has associated bidders, send info to update dialog for final confirmation and action
      if (!isEmpty(preset.BidderIDs)) {
        unstable_batchedUpdates(() => {
          setUpdatePayload(finalPayload);
          setOpenUpdateDialog(true);
        });
      } else {
        patchTargetingPresetHeaderBidder(session.publisher.ID, finalPayload)
          .then((res) => {
            if (res.Preset) {
              const completePreset = applyPresetCustoms(res.Preset);
              unstable_batchedUpdates(() => {
                setSeed(seed + 1);
                setPreset(completePreset || ({} as ITargetingPresetHeaderBidder));
                setName(res.Preset?.Name || '');
                setSnackbarMessage(
                  `Preset ${res.Preset?.Name || ''} has been successfully updated.`,
                );
                setSnackbarError(false);
                setFormErrors([]);
                setIsSaveDisabled(false);
              });
            } else {
              unstable_batchedUpdates(() => {
                setSnackbarMessage('An error has occurred.');
                setSnackbarError(true);
                setIsSaveDisabled(false);
              });
            }
          })
          .catch((err) => {
            console.error(err);
            unstable_batchedUpdates(() => {
              setSnackbarMessage('An error has occurred. Please check your values and try again.');
              setSnackbarError(true);
              setIsSaveDisabled(false);
            });
          });
      }
    }
  };

  const handleUpdateSuccess = (updatedPreset: ITargetingPresetHeaderBidder) => {
    const completePreset = applyPresetCustoms(updatedPreset);
    unstable_batchedUpdates(() => {
      setSeed(seed + 1);
      setPreset(completePreset || ({} as ITargetingPresetHeaderBidder));
      setName(updatedPreset.Name || '');
      setSnackbarMessage(`Preset ${updatedPreset.Name || ''} has been successfully updated.`);
      setSnackbarError(false);
      setFormErrors([]);
      setIsSaveDisabled(false);
      setOpenUpdateDialog(false);
    });
  };

  const handleCloseUpdateDialog = () => {
    unstable_batchedUpdates(() => {
      setOpenUpdateDialog(false);
      setIsSaveDisabled(false);
    });
  };

  const getBidderSettingsRoute = (id: string): string => {
    return getRoutePath(RouteBidderSettingsExisting, extractRouteParams({ session, user }), {
      b: id,
    });
  };

  const isLoaded = loadState !== LoadingStateLoading && listsLoadState !== LoadingStateLoading;
  if (loadState === LoadingStateLoading) return null;

  return (
    <BidderTargetingPresetFormContext.Provider value={{ lists }}>
      <Panels max vertical relative minWidthMain>
        <Panel largeHorizontalPadding smallVerticalPadding>
          <TopBar title='Targeting Preset' enableUINotices />
        </Panel>
        <Panel
          largeHorizontalPadding
          smallVerticalPadding
          style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
        >
          {/* TITLE - SAVE CLOSE */}
          <Cols
            gap={spacing.base}
            style={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Col flex>
              <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                <Col displayFlex>
                  <IconV2 name='new' large />
                </Col>
                <Col>
                  <Text extraLarge bold>
                    {preset.Name || 'New Preset'}
                  </Text>
                </Col>
              </Cols>
            </Col>
            <Col>
              <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                <Col>
                  <ButtonV2 small onClick={handleOnSave} disabled={!isLoaded || isSaveDisabled}>
                    <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                      <Col displayFlex paddingBottom='1px'>
                        <IconV2
                          name='save'
                          color={!isLoaded ? publicaColors.gray : publicaColors.white}
                          small
                        />
                      </Col>
                      <Col>Save</Col>
                    </Cols>
                  </ButtonV2>
                </Col>
                <Col>
                  <ButtonV2
                    small
                    style={{ paddingLeft: '13px' }}
                    clearBackground
                    onClick={() => navigate(RouteTargetingList, { p: session.publisher.ID })}
                  >
                    <Cols gap='2px' style={{ alignItems: 'center' }}>
                      <Col displayFlex paddingBottom='1px'>
                        <IconV2 name='close' color={publicaColors.gray} large />
                      </Col>
                      <Col>Close</Col>
                    </Cols>
                  </ButtonV2>
                </Col>
              </Cols>
            </Col>
          </Cols>
        </Panel>
        {/* NOTES */}
        <Panel scroll largeHorizontalPadding paddingTop={spacing.large} paddingBottom='48px'>
          <Rows gap={spacing.base}>
            {isNewPreset && (
              <Row noPadding>
                <Cols gap={spacing.small}>
                  <Col>
                    <Text
                      small
                      style={{
                        backgroundColor: publicaColors.ivory,
                        borderRadius: '6px',
                        padding: '4px 8px',
                      }}
                    >
                      <Span style={{ paddingRight: spacing.small }}>
                        <Div style={{ display: 'inline', top: '2px' }}>
                          <IconV2 name='info-static' small />
                        </Div>
                      </Span>
                      Start building a preset by picking parameters and combining it with logic to
                      achieve a desired targeting preset. Reuse it for Bidders, soon Campaigns, and
                      directly in Forecasting.
                    </Text>
                  </Col>
                </Cols>
              </Row>
            )}
            {/* EDIT NAME */}
            <Row noPadding height={labelRowHeight}>
              <Cols style={{ alignItems: 'center' }}>
                {/* LABEL */}
                <Col width='160px'>
                  <Text small bold>
                    Preset Name
                  </Text>
                </Col>
                <Col>
                  <TextInput
                    placeholder='New Preset'
                    minLength={3}
                    maxLength={99}
                    isAutoFocused={isNewPreset}
                    handleOnChange={(text) => setName(text)}
                    inputSize='small'
                    width='400px'
                    initialValue={name}
                    key={seed}
                    inputID='presetname'
                  />
                </Col>
              </Cols>
            </Row>
            {/* NUMBER ASSOCIATED BIDDERS */}
            <Row noPadding height={labelRowHeight}>
              <Cols style={{ alignItems: 'center' }}>
                {/* LABEL */}
                <Col width='160px'>
                  <Text small bold>
                    Targeted Bidders
                  </Text>
                </Col>
                <Col
                  onClick={() => setOpenAssociatedBidders(true)}
                  style={{ cursor: !isEmpty(preset.Bidders) ? 'pointer' : 'default' }}
                >
                  <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                    <Col
                      style={{
                        border: `1px solid ${publicaColors.veryDarkGray}`,
                        borderRadius: '4px',
                        backgroundColor: publicaColors.honeydew,
                        padding: `0 6px`,
                      }}
                    >
                      <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
                        <Col displayFlex>
                          <IconV2 name='bidders' small />
                        </Col>
                        <Col>
                          <Text small bold>
                            {preset?.BidderIDs ? preset?.BidderIDs.length : 0}
                          </Text>
                        </Col>
                      </Cols>
                    </Col>
                  </Cols>
                </Col>
              </Cols>
            </Row>
            {/* EDIT LOGIC */}
            <Row noPadding height={labelRowHeight}>
              <Cols style={{ alignItems: 'center' }}>
                {/* LABEL */}
                <Col width='160px'>
                  <Text small bold>
                    Combining Logic
                  </Text>
                </Col>
                <Col>
                  <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                    <Col
                      style={{
                        border: `1px solid ${publicaColors.veryDarkGray}`,
                        borderRadius: '4px',
                        backgroundColor: publicaColors.lavender,
                        padding: `0 6px 0 4px`,
                      }}
                    >
                      <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
                        <Col displayFlex>
                          <IconV2 name='venn' large />
                        </Col>
                        <Col>
                          <Text small bold>
                            AND
                          </Text>
                        </Col>
                      </Cols>
                    </Col>
                    <Col>
                      <Text
                        extraSmall
                        color={publicaColors.black}
                        style={{
                          borderRadius: '4px',
                          backgroundColor: publicaColors.lightGray,
                          padding: `0 ${spacing.extraSmall}`,
                        }}
                      >
                        Once targeted, Preset will be hit whenever it matches{' '}
                        <Span heavy italic>
                          all
                        </Span>{' '}
                        of the following parameters.
                      </Text>
                    </Col>
                  </Cols>
                </Col>
              </Cols>
            </Row>
            {/* PRESET TARGETING PARAMS */}
            <Row
              noPadding
              style={{
                paddingTop: spacing.large,
                borderTop: `1px solid ${publicaColors.lightGray}`,
              }}
            >
              <Rows>
                <Row noPadding height={labelRowHeight}>
                  <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                    <Col displayFlex width={iconColWidth} paddingLeft='1px'>
                      <IconV2 name='settings' small />
                    </Col>
                    <Col>
                      <Text small bold>
                        Preset Targeting Parameters
                      </Text>
                    </Col>
                  </Cols>
                </Row>
                {/* CUSTOM ERRORS */}
                {!isEmpty(formErrors) && (
                  <Row smallVerticalPadding largeHorizontalPadding>
                    <Rows>
                      {formErrors.map((formError) => (
                        <Row noPadding>
                          <Text extraSmall color={publicaColors.red}>
                            <>
                              <Span style={{ paddingRight: spacing.small }}>•</Span>
                            </>
                            {formError}
                          </Text>
                        </Row>
                      ))}
                    </Rows>
                  </Row>
                )}
                <Row smallVerticalPadding largeHorizontalPadding>
                  <FormBuilder
                    object={preset}
                    defaultObject={defaultTargetingPresetHeaderBidder}
                    parameterOptions={generateTargetingPresetParameterOptions(
                      session.publisher.ID,
                      isSuperUser,
                    )}
                    parameterCfg={TargetingPresetParameters}
                    lists={lists}
                    initialParameterKeys={(preset?.ActiveParameters || '').split(',')}
                    isLoading={!isLoaded}
                    key={seed}
                  />
                </Row>
              </Rows>
            </Row>
          </Rows>
        </Panel>
        <UpdateTargetingPresetBidderDialog
          open={openUpdateDialog}
          handleClose={handleCloseUpdateDialog}
          preset={updatePayload}
          handleSuccess={handleUpdateSuccess}
          publisherID={session.publisher.ID}
          setSnackbarError={setSnackbarError}
          setSnackbarMessage={setSnackbarMessage}
        />
        {!isEmpty(preset?.Bidders) && (
          <IDNameTableModal
            open={openAssociatedBidders}
            onClose={() => setOpenAssociatedBidders(false)}
            title='Associated Bidders'
            subTitle={preset.Name}
            data={preset?.Bidders || []}
            getItemSettingsRoute={getBidderSettingsRoute}
          />
        )}
        <SnackbarView
          open={!!snackbarMessage}
          error={!!snackbarError}
          onClose={() => setSnackbarMessage('')}
        >
          {snackbarMessage}
        </SnackbarView>
      </Panels>
    </BidderTargetingPresetFormContext.Provider>
  );
};
